import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components/icon/Icon';

import { Tooltip, TooltipTrigger } from '../tooltip/Tooltip';
import { NavItem } from './components/nav-item/NavItem';
import * as S from './SideNavigation.styles';

export interface SideNavigationProps {
  activeSubKey?: string;
  data: Category;
  className?: string;
  isExpanded?: boolean;
  onSidebarToggle?: () => void;
  expandable?: boolean;
  showSeparator?: boolean;
  createNewButton?: boolean;
}

export interface CategoryItem {
  label: string;
  key: string;
  icon?: ReactNode;
  badge?: ReactNode;
  disabled?: boolean;
  onClick: (item: CategoryItem) => void;
  borderBottom?: boolean;
  notification?: ReactNode;
}

export interface Category {
  id?: string;
  title?: string;
  key: string;
  createNewLabel?: string;
  items: CategoryItem[];
  bottomItems?: CategoryItem[];
  expandable?: boolean;
  showSeparator?: boolean;
  createNewButton?: boolean;
  onClick?: () => void;
}

export const SideNavigation = ({
  activeSubKey,
  data,
  className,
  isExpanded = true,
  onSidebarToggle,
  expandable = true,
  showSeparator = true,
  createNewButton = true,
}: SideNavigationProps) => {
  const { t } = useTranslation('common');
  const createNewLabel = data.createNewLabel;

  return (
    <S.AsideExpandable
      className={className}
      data-testid="side-navigation"
      $isExpanded={isExpanded}
    >
      <S.ToggleButtonWrapper>
        {isExpanded && <S.Header>{data.title}</S.Header>}
        {expandable && (
          <Tooltip
            label={
              isExpanded
                ? t('common:sideNavigation.hideMenu')
                : t('common:sideNavigation.showMenu')
            }
            trigger={TooltipTrigger.Hover}
            placement="right"
          >
            <S.ToggleButton
              $isExpanded={isExpanded}
              onClick={onSidebarToggle}
              data-testid={'button-toggle'}
            >
              <Icon iconName="ArrowRight" size={16} />
            </S.ToggleButton>
          </Tooltip>
        )}
      </S.ToggleButtonWrapper>

      {!isExpanded && createNewButton && (
        <Tooltip
          label={createNewLabel}
          trigger={TooltipTrigger.Hover}
          placement="right"
        >
          <S.ActionButton
            onClick={data.onClick}
            data-testid={'sidebar-add-button'}
          >
            <Icon iconName="Add" size={16} />
          </S.ActionButton>
        </Tooltip>
      )}

      {isExpanded && createNewLabel && createNewButton && (
        <S.ActionButtonWrapper
          onClick={data.onClick}
          data-testid={'sidebar-add-button-expanded'}
        >
          <Icon iconName="Add" size={16} />
          {createNewLabel}
        </S.ActionButtonWrapper>
      )}

      {showSeparator && <S.Separator />}

      <S.Categories $isExpanded={isExpanded}>
        {data.items.map(item => {
          return (
            <NavItem
              key={item.key}
              item={item}
              activeSubKey={activeSubKey}
              sideBarExpanded={isExpanded}
            />
          );
        })}
      </S.Categories>

      <S.Categories $isExpanded={isExpanded} $bottom>
        {data.bottomItems?.map(item => {
          return (
            <NavItem
              key={item.key}
              item={item}
              activeSubKey={activeSubKey}
              sideBarExpanded={isExpanded}
            />
          );
        })}
      </S.Categories>
    </S.AsideExpandable>
  );
};
